import {
  createContext,
  useState,
  useMemo,
  useCallback,
  useContext,
  useEffect,
} from "react";

// Create the context
export const FocusContext = createContext();

// Custom hook to use this context
export function useFocusContext() {
  const context = useContext(FocusContext);
  if (!context) {
    throw new Error("useFocusContext must be used within a FocusContextProvider");
  }
  return context;
}

const readCollectionIdFromUrl = () => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("collectionId");
};

// Provider component
function FocusContextProvider({ children }) {
  const [focusCollection, setFocusCollection] = useState(readCollectionIdFromUrl);
  const [focusedEntityId, setFocusedEntityId] = useState(null);
  const [focusedGroupId, setFocusedGroupId] = useState(null);
  const [focusCameraNonce, setFocusCameraNonce] = useState(0);
  const [collapseDetailsPanelNonce, setCollapseDetailsPanelNonce] = useState(0);

  const updateFocusCollection = useCallback((collKey) => {
    setFocusCollection(collKey);
    // When collection changes, clear focused group and entity
    setFocusedGroupId(null);
    setFocusedEntityId(null);
  }, []);

  const updateFocusedEntityId = useCallback((id) => {
    setFocusedEntityId(id);
  }, []);

  // Always recenters, even when this entity is already selected.
  const requestFocusEntity = useCallback((id) => {
    if (id == null || id === "") return;
    setFocusedEntityId(id);
    setFocusCameraNonce((n) => n + 1);
  }, []);

  const updateFocusedGroupId = useCallback((id) => {
    setFocusedGroupId(id);
    // When group selection changes, clear entity focus
    setFocusedEntityId(null);
  }, []);

  const requestCollapseDetailsPanel = useCallback(() => {
    setCollapseDetailsPanelNonce((n) => n + 1);
  }, []);


  // Create a stable context value object to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      focusCollection,
      updateFocusCollection,
      focusedEntityId,
      updateFocusedEntityId,
      requestFocusEntity,
      focusCameraNonce,
      focusedGroupId,
      updateFocusedGroupId,
      collapseDetailsPanelNonce,
      requestCollapseDetailsPanel,
    }),
    [
      focusCollection,
      focusedEntityId,
      focusedGroupId,
      focusCameraNonce,
      updateFocusCollection,
      updateFocusedEntityId,
      requestFocusEntity,
      updateFocusedGroupId,
      collapseDetailsPanelNonce,
      requestCollapseDetailsPanel,
    ]
  );

  return (
    <FocusContext.Provider value={contextValue}>
      {children}
    </FocusContext.Provider>
  );
}

export default FocusContextProvider; 